// src/pages/ranking/TotalRankingList.jsx

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { apiFetch } from '../../utils/api';
import { getCategories } from '../../utils/categoryApi';

// Color Admin スタイル定義
const customDataTableStyles = {
  table: { style: { backgroundColor: 'transparent' } },
  headRow: {
    style: {
      backgroundColor: 'transparent',
      borderBottom: '2px solid #dadfc1',
      minHeight: '45px',
    },
  },
  headCells: {
    style: {
      fontSize: '13px',
      fontWeight: 'bold',
      color: '#1f2d3d',
      paddingLeft: '16px',
      paddingRight: '16px',
    },
  },
  rows: {
    style: {
      fontSize: '13px',
      color: '#212529',
      minHeight: '70px',
      borderBottom: '1px solid #e3e6ec',
      backgroundColor: '#ffffff',
    },
    stripedStyle: {
      backgroundColor: '#f8f9fa',
      color: '#212529',
    },
    highlightOnHoverStyle: {
      backgroundColor: '#e9ecef !important',
      color: '#212529',
      transitionDuration: '0.1s',
      transitionProperty: 'background-color',
      borderBottomColor: '#e3e6ec',
      outline: 'none',
    },
  },
  pagination: {
    style: { borderTop: '1px solid #e3e6ec', fontSize: '13px', color: '#495057' },
  },
  cells: {
    style: { display: 'flex', alignItems: 'center' },
  },
};

const TotalRankingList = () => {
  const navigate = useNavigate();

  const [days, setDays] = useState(7);
  const [limit, setLimit] = useState(50);

  // カテゴリ用状態
  const [largeCategories, setLargeCategories] = useState([]);
  const [selectedCategoryTab, setSelectedCategoryTab] = useState(''); // 空文字 = すべての商品

  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(false);

  // 同期実行中状態（指定された days または 'current' を保持）
  const [syncingDays, setSyncingDays] = useState(null);

  // DataTable用のカラム定義
  const columns = [
    {
      name: '順位',
      selector: row => row.rank,
      sortable: true,
      width: '90px',
      cell: row => (
        <div className="text-center w-100">
          <span className={`badge fs-14px ${row.rank <= 3 ? 'bg-warning text-dark fw-bold' : 'bg-secondary'}`}>
            {row.rank}位
          </span>
        </div>
      ),
    },
    {
      name: '画像',
      width: '110px',
      cell: row => {
        const defaultImage = "/assets/img/brand/shop.svg";
        const productUuid = row.master_product_uuid || row.product_uuid || row.uuid || row.master_uuid;

        return (
          <div
            role="button"
            tabIndex={0}
            onClick={() => productUuid && navigate(`/purchase/products/edit/${productUuid}`)}
            onKeyDown={(e) => e.key === 'Enter' && productUuid && navigate(`/purchase/products/edit/${productUuid}`)}
            style={{ cursor: productUuid ? 'pointer' : 'default' }}
            title={productUuid ? "クリックして商品詳細へ移動" : "詳細UUIDが見つかりません"}
          >
            <img
              src={row.image_url || defaultImage}
              alt={row.product_name || "商品イメージ"}
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'cover',
                borderRadius: '4px',
                border: '1px solid #eee',
                transition: 'transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
              }}
              className="img-thumbnail-hover"
              onError={(e) => { e.target.src = defaultImage; }}
            />
          </div>
        );
      }
    },
    {
      name: '品番 / 商品名',
      selector: row => row.product_name,
      wrap: true,
      cell: row => (
        <div style={{ padding: '12px 0', width: '100%' }}>
          <span className="fw-bold fs-13px text-dark lh-sm d-block mb-1">{row.model_code}</span>
          <div className="fs-12px text-muted mt-1">{row.product_name}</div>
        </div>
      ),
    },
    {
      name: <div className="text-end w-100">販売数量</div>,
      selector: row => row.total_quantity,
      sortable: true,
      width: '130px',
      cell: row => <div className="fw-bold text-end w-100">{row.total_quantity?.toLocaleString()} 点</div>
    },
    {
      name: <div className="text-end w-100">売上金額</div>,
      selector: row => row.total_sales_amount,
      sortable: true,
      width: '160px',
      cell: row => <div className="fw-bold text-dark text-end w-100">¥{row.total_sales_amount?.toLocaleString()}</div>
    }
  ];

  // 初期化：大分類マスターデータの取得
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cData = await getCategories();
        setLargeCategories(cData.filter(c => !c.parent_uuid));
      } catch (e) {
        console.error('カテゴリマスターの読込に失敗しました', e);
      }
    };
    loadCategories();
  }, []);

  // ランキングデータの取得
  const fetchRankings = async () => {
    setLoading(true);
    try {
      const url = `/rankings/total?days=${days}&limit=${limit}&large_category_uuid=${selectedCategoryTab}`;
      const response = await apiFetch(url);

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();
      setRankings(data.items || []);
    } catch (error) {
      console.error("総合売上ランキングデータの取得に失敗しました:", error);
      setRankings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRankings();
  }, [days, limit, selectedCategoryTab]);

  // Shopifyへランキングデータを送信（同期）する処理
  const handleSyncToShopify = async (targetDays, label) => {
    if (syncingDays !== null) return;

    const categoryName = largeCategories.find(c => c.uuid === selectedCategoryTab)?.name || 'すべて';
    const confirmMessage = `【Shopify送信確認】\n対象期間: ${label}\n上位件数: ${limit}件\nカテゴリ: ${categoryName}\n\nShopifyのメタオブジェクトへランキングデータを送信しますか？`;

    if (!window.confirm(confirmMessage)) return;

    setSyncingDays(targetDays);
    try {
      let url = `/rankings/official/sync?days=${targetDays}&limit=${limit}`;
      if (selectedCategoryTab) {
        url += `&large_category_uuid=${selectedCategoryTab}`;
      }

      const response = await apiFetch(url, { method: 'POST' });

      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || '送信処理に失敗しました');
      }

      const result = await response.json();
      alert(`✅ Shopifyへの送信が完了しました！\n（対象期間: ${label}）`);
    } catch (error) {
      console.error("Shopify送信エラー:", error);
      alert(`❌ 送信に失敗しました: ${error.message}`);
    } finally {
      setSyncingDays(null);
    }
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item active">総合売上ランキング</li>
      </ol>
      <h1 className="page-header">
        全モール総合売上ランキング <small>全社データ集計</small>
      </h1>

      <div className="card border-0 shadow-sm">
        <ul className="nav nav-tabs nav-tabs-v2 px-3 pt-2">
          <li className="nav-item me-2">
            <button
              className={`nav-link px-3 py-2 fw-bold ${selectedCategoryTab === '' ? 'active' : ''}`}
              onClick={() => setSelectedCategoryTab('')}
            >
              📦 すべての商品
            </button>
          </li>
          {largeCategories.map(cat => (
            <li className="nav-item me-2" key={cat.uuid}>
              <button
                className={`nav-link px-3 py-2 fw-bold ${selectedCategoryTab === cat.uuid ? 'active text-primary' : ''}`}
                onClick={() => setSelectedCategoryTab(cat.uuid)}
              >
                {cat.name}
              </button>
            </li>
          ))}
        </ul>

        <div className="tab-content p-3">
          <div className="d-flex mb-3 gap-3 align-items-center flex-wrap">
            <select
              className="form-select w-auto fw-bold"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <option value={1}>Daily</option>
              <option value={7}>Weekly</option>
              <option value={30}>Monthly</option>
              <option value={90}>3 Month</option>
            </select>

            <select
              className="form-select w-auto fw-bold"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
            >
              <option value={20}>上位 20 件表示</option>
              <option value={50}>上位 50 件表示</option>
              <option value={100}>上位 100 件表示</option>
            </select>

            {/* Shopify同期アクションエリア */}
            <div className="ms-auto d-flex gap-2">
              <div className="dropdown">
                <button
                  className="btn btn-outline-success fw-bold dropdown-toggle d-flex align-items-center"
                  type="button"
                  id="shopifySyncDropdown"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                  disabled={syncingDays !== null}
                >
                  {syncingDays !== null ? (
                    <>
                      <i className="fa fa-spinner fa-spin me-2"></i>
                      送信中...
                    </>
                  ) : (
                    <>
                      {/* 🌟 text-success を削除し、ボタンのホバー色変化に追従させます */}
                      <i className="fab fa-shopify me-2 fs-16px"></i>
                      Shopifyへ送信
                    </>
                  )}
                </button>
                <ul className="dropdown-menu dropdown-menu-end shadow border-0" aria-labelledby="shopifySyncDropdown">
                  <li>
                    <h6 className="dropdown-header text-uppercase fw-bold">期間を指定して送信</h6>
                  </li>
                  <li>
                    <button
                      className="dropdown-item fw-bold py-2 d-flex align-items-center"
                      onClick={() => handleSyncToShopify(1, 'Daily (1日)')}
                    >
                      <i className="fa fa-paper-plane text-muted me-2 w-20px"></i> Daily (1日) を送信
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item fw-bold py-2 d-flex align-items-center"
                      onClick={() => handleSyncToShopify(7, 'Weekly (7日)')}
                    >
                      <i className="fa fa-paper-plane text-muted me-2 w-20px"></i> Weekly (7日) を送信
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item fw-bold py-2 d-flex align-items-center"
                      onClick={() => handleSyncToShopify(30, 'Monthly (30日)')}
                    >
                      <i className="fa fa-paper-plane text-muted me-2 w-20px"></i> Monthly (30日) を送信
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item fw-bold py-2 d-flex align-items-center"
                      onClick={() => handleSyncToShopify(90, '3 Month (90日)')}
                    >
                      <i className="fa fa-paper-plane text-muted me-2 w-20px"></i> 3 Month (90日) を送信
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <DataTable
            columns={columns}
            data={rankings}
            progressPending={loading}
            highlightOnHover
            striped
            responsive
            customStyles={customDataTableStyles}
            noDataComponent={
              <div className="text-center p-5 text-muted">
                <i className="fa fa-chart-line fa-2x mb-2 d-block"></i>
                該当するカテゴリの集計データが存在しません
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default TotalRankingList;